import {
  getV1CompaniesCompanyIdProjectsTypeahead,
  Project_Read_Typeahead,
  ProjectState,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

import AutocompleteBase from '~components/FormFields/v2/Autocomplete/AutocompleteBase';
import {
  AutocompleteBaseProps,
  AutocompleteFieldOption,
} from '~components/FormFields/v2/Autocomplete/types';
import useAsyncAutocompleteField from '~components/FormFields/v2/Autocomplete/useAsyncAutocompleteField';
import { ProjectTypeahead } from '~hooks/useProjects/models';

const INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX = 300;
const NO_EXCLUDED_IDS: string[] = [];

type Props = {
  companyId: string;
  excludeIds?: string[];
  label?: string;
  onChange: (option: AutocompleteFieldOption<ProjectTypeahead> | null) => void;
  value: AutocompleteFieldOption<ProjectTypeahead> | null;
};

export default function AddPhasesProjectSelector({
  companyId,
  excludeIds = NO_EXCLUDED_IDS,
  label,
  onChange,
  value,
}: Props) {
  const autocompleteState = useAsyncAutocompleteField({
    fetchFn: {
      arguments: {
        path: { 'company-id': companyId },
        query: { 'filter[states]': [ProjectState.ACTIVE] },
      },
      request: getV1CompaniesCompanyIdProjectsTypeahead,
    },
    parseFn: (item: Project_Read_Typeahead) => {
      const parsed = ProjectTypeahead.parse(item);

      return { _entity: parsed, label: parsed.name, value: parsed.id };
    },
    queryName: `AddPhasesProjects_${companyId}`,
  });

  const options = useMemo(
    () =>
      autocompleteState.options.filter((option) => !excludeIds.includes(option.value)),
    [autocompleteState.options, excludeIds],
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget as HTMLElement;
      const scrollThreshold = target.scrollHeight - INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX;
      const scrolledOffset = target.scrollTop + target.clientHeight;

      if (scrolledOffset >= scrollThreshold) {
        autocompleteState.fetchMore();
      }
    },
    [autocompleteState.fetchMore],
  );

  const handleChange = useCallback<
    AutocompleteBaseProps<FieldValues, ProjectTypeahead>['onChange']
  >(
    (option) => {
      if (!Array.isArray(option)) {
        onChange(option);
      }
    },
    [onChange],
  );

  return (
    <AutocompleteBase<FieldValues, ProjectTypeahead>
      ListboxProps={{ onScroll: handleScroll }}
      inputValue={autocompleteState.inputValue}
      isLoading={autocompleteState.isFetching}
      isOpen={autocompleteState.isOpen}
      label={label}
      name="addPhasesProject"
      onChange={handleChange}
      onClose={autocompleteState.handleClose}
      onInputChange={autocompleteState.handleInputChange}
      onOpen={autocompleteState.handleOpen}
      options={options}
      value={value}
    />
  );
}
